import { api } from '@/lib/api';
import { toast } from 'sonner';
import { getFieldLabel } from '@/lib/fieldLabel';
import { getBrandName } from '@/lib/branding';

// Lets the browser paint the "Exporting…" state and stay responsive before a
// blocking html2canvas capture, and between captures in the multi-slide path.
const yieldToMain = () => new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));

/**
 * Handles exporting report data in various formats.
 * Returns an async function to be called by the component.
 */
export async function handleExportData({
  result, exportFormat, exportSettings, reportName, reportDesc, modelId,
  rowDims, colDim, valueMeasures, allFields, fieldOverrides, visuals,
  setExporting, setExportOpen
}) {
  if (!result?.data?.length) { toast.error('No data to export'); return; }
  setExporting(true);
  try {
    const data = result.data;
    const cols = Object.keys(data[0]);
    const title = reportName || 'Report';
    const getHeader = (k) => { const ovr = fieldOverrides[k]; const f = allFields.find(ff => ff.name === k); return getFieldLabel(f, k, ovr); };
    const headers = cols.map(getHeader);

    if (exportFormat === 'excel') {
      const blob = await api.exportExcel({ model_id: modelId, dimensions: [...rowDims, ...(colDim ? [colDim] : [])], measures: valueMeasures, title });
      const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = title + '.xlsx'; a.click();
    } else if (exportFormat === 'pdf' || exportFormat === 'word') {
      // Build a real HTML data table from the full result set. Previously the
      // export relied ONLY on an html2canvas screenshot of the on-screen canvas,
      // but the table lives in an `overflow:auto` scroll box so only the visible
      // slice was captured — producing a near-blank PDF. The screenshot is now
      // kept purely for chart visuals; tabular data always renders in full.
      const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const fmtCell = (v) => v === null || v === undefined ? '' : (typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v));
      const buildTableHtml = () => {
        const th = headers.map(h => `<th>${esc(h)}</th>`).join('');
        const rows = data.map(row => '<tr>' + cols.map(c => {
          const v = row[c];
          const align = typeof v === 'number' ? ' style="text-align:right"' : '';
          return `<td${align}>${esc(fmtCell(v))}</td>`;
        }).join('') + '</tr>').join('');
        return `<table><thead><tr>${th}</tr></thead><tbody>${rows}</tbody></table>`;
      };
      const TABLE_TYPES = ['table', 'pivot', 'comparison_table', 'kpi', 'rich_text', 'shape', 'image'];
      const hasChartVisual = (visuals || []).some(v => v && v.vizType && !TABLE_TYPES.includes(v.vizType));
      let imgDataUrl = null;
      if (hasChartVisual) {
        const canvasEl = document.querySelector('[data-testid="visual-canvas"]')
          || document.querySelector('.react-grid-layout');
        if (canvasEl) {
          try {
            const tId = toast.loading('Rendering chart…');
            await yieldToMain();
            const { default: html2canvas } = await import('html2canvas');
            const canvas = await html2canvas(canvasEl, { backgroundColor: '#ffffff', scale: 2, logging: false, useCORS: true });
            imgDataUrl = canvas.toDataURL('image/png');
            toast.dismiss(tId);
          } catch (err) { console.error('Screenshot failed:', err); }
        }
      }
      if (exportFormat === 'pdf') {
        let htmlContent = '<html><head><meta charset="utf-8"><style>'
          + 'body{font-family:sans-serif;margin:40px;background:#fff;color:#000}'
          + 'h1{font-size:20px;margin-bottom:4px}p{font-size:12px;color:#666}'
          + 'img{max-width:100%;border:1px solid #eee;border-radius:8px;margin:16px 0}'
          + 'table{border-collapse:collapse;width:100%;margin-top:16px;font-size:11px}'
          + 'th,td{border:1px solid #ddd;padding:5px 8px;text-align:left}'
          + 'thead th{background:#e8f0fe;color:#1a1a2e;font-weight:600}'
          + 'thead{display:table-header-group}tr{page-break-inside:avoid}'
          + '</style></head><body>';
        htmlContent += '<h1>' + esc(title) + '</h1>';
        if (exportSettings.subtitle) htmlContent += '<p>' + esc(exportSettings.subtitle) + '</p>';
        if (imgDataUrl) htmlContent += '<img src="' + imgDataUrl + '" />';
        htmlContent += buildTableHtml();
        htmlContent += '<p style="font-size:10px;color:#aaa;margin-top:24px">Generated ' + new Date().toLocaleString() + ' | ' + result.row_count + ' rows</p>';
        htmlContent += '</body></html>';
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        const w = window.open(blobUrl, '_blank');
        if (w) setTimeout(() => { w.print(); URL.revokeObjectURL(blobUrl); }, 500);
      } else {
        let html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"><style>'
          + 'body{font-family:Calibri,sans-serif;margin:1in}h1{font-size:18pt}img{max-width:100%}'
          + 'table{border-collapse:collapse;width:100%;margin-top:12pt;font-size:9pt}'
          + 'th,td{border:1px solid #bbb;padding:4pt 6pt;text-align:left}'
          + 'thead th{background:#e8f0fe;color:#1a1a2e;font-weight:bold}'
          + '</style></head><body>';
        html += '<h1>' + esc(title) + '</h1>';
        if (imgDataUrl) html += '<img src="' + imgDataUrl + '" />';
        html += buildTableHtml();
        if (exportSettings.includeTimestamp) html += '<p style="font-size:9pt;color:#888">Generated ' + new Date().toLocaleString() + '</p>';
        html += '</body></html>';
        const blob = new Blob([html], { type: 'application/msword' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = title + '.doc'; a.click();
      }
    } else if (exportFormat === 'csv') {
      const delim = exportSettings.delimiter || ',';
      const lines = [headers.join(delim)];
      data.forEach(row => lines.push(cols.map(c => { const v = row[c]; return v === null || v === undefined ? '' : typeof v === 'string' && v.includes(delim) ? '"' + v + '"' : String(v); }).join(delim)));
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = title + '.csv'; a.click();
    } else if (exportFormat === 'json') {
      const indent = exportSettings.prettyPrint ? 2 : 0;
      const blob = new Blob([JSON.stringify(data, null, indent)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = title + '.json'; a.click();
    } else if (exportFormat === 'xml') {
      let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<report name="' + title + '">\n';
      data.forEach(row => { xml += '  <row>\n'; cols.forEach(c => { xml += '    <' + c + '>' + (row[c] !== null && row[c] !== undefined ? String(row[c]) : '') + '</' + c + '>\n'; }); xml += '  </row>\n'; });
      xml += '</report>';
      const blob = new Blob([xml], { type: 'application/xml' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = title + '.xml'; a.click();
    } else if (exportFormat === 'markdown') {
      let md = '# ' + title + '\n\n';
      md += '| ' + headers.join(' | ') + ' |\n';
      md += '| ' + headers.map(() => '---').join(' | ') + ' |\n';
      data.forEach(row => { md += '| ' + cols.map(c => String(row[c] !== null && row[c] !== undefined ? row[c] : '')).join(' | ') + ' |\n'; });
      const blob = new Blob([md], { type: 'text/markdown' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = title + '.md'; a.click();
    } else if (exportFormat === 'pptx') {
      if (!window.PptxGenJS) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/pptxgenjs@4.0.1/dist/pptxgen.bundle.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      const pptx = new window.PptxGenJS();
      pptx.author = getBrandName();
      pptx.title = title;
      const titleSlide = pptx.addSlide();
      titleSlide.addText(title, { x: 0.5, y: 1.5, w: 9, h: 1.5, fontSize: 28, bold: true, color: '1a1a2e' });
      if (reportDesc) titleSlide.addText(reportDesc, { x: 0.5, y: 3, w: 9, h: 1, fontSize: 14, color: '666666' });
      titleSlide.addText('Generated ' + new Date().toLocaleString(), { x: 0.5, y: 4.5, w: 9, h: 0.5, fontSize: 10, color: '999999' });
      const dataSlide = pptx.addSlide();
      dataSlide.addText(title, { x: 0.3, y: 0.2, w: 9, h: 0.5, fontSize: 18, bold: true, color: '1a1a2e' });
      const tableRows = [headers.map(h => ({ text: h, options: { bold: true, fontSize: 8, fill: 'e8f0fe', color: '1a1a2e' } }))];
      data.slice(0, 30).forEach(row => {
        tableRows.push(cols.map(c => ({ text: String(row[c] !== null && row[c] !== undefined ? (typeof row[c] === 'number' ? row[c].toLocaleString(undefined, { maximumFractionDigits: 2 }) : row[c]) : ''), options: { fontSize: 7, color: '333333' } })));
      });
      dataSlide.addTable(tableRows, { x: 0.3, y: 0.8, w: 9.4, colW: Array(cols.length).fill(9.4 / cols.length), border: { pt: 0.5, color: 'cccccc' }, autoPage: true });
      const chartVisuals = visuals.filter(v => !['rich_text', 'shape', 'image'].includes(v.vizType));
      let vIdx = 0;
      const pptxToast = chartVisuals.length ? toast.loading(`Rendering slide 1 of ${chartVisuals.length}…`) : null;
      for (const vis of chartVisuals) {
        vIdx += 1;
        if (pptxToast) toast.loading(`Rendering slide ${vIdx} of ${chartVisuals.length}…`, { id: pptxToast });
        const el = document.querySelector(`[data-testid="visual-${vis.id}"]`);
        if (el) {
          try {
            await yieldToMain();
            const { default: html2canvas } = await import('html2canvas');
            const canvas = await html2canvas(el, { scale: 2, logging: false, backgroundColor: '#ffffff', useCORS: true });
            const imgUrl = canvas.toDataURL('image/png');
            const slide = pptx.addSlide();
            slide.addText(vis.title || 'Visual', { x: 0.3, y: 0.2, w: 9, h: 0.4, fontSize: 14, bold: true, color: '1a1a2e' });
            slide.addImage({ data: imgUrl, x: 0.3, y: 0.7, w: 9.4, h: 5.5, sizing: { type: 'contain', w: 9.4, h: 5.5 } });
          } catch {}
        }
      }
      if (pptxToast) toast.dismiss(pptxToast);
      const pptxBlob = await pptx.write({ outputType: 'blob' });
      const url = URL.createObjectURL(new Blob([pptxBlob], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' })); const a = document.createElement('a'); a.href = url; a.download = title + '.pptx'; a.click();
    }
    toast.success('Exported as ' + exportFormat.toUpperCase());
    setExportOpen(false);
  } catch (err) { toast.error(err.message); }
  finally { setExporting(false); }
}
